import apiClient from './api.js'
import { env } from '@/utils/env.js'
import { logger } from '@/utils/logger.js'

/**
 * Base API Service Class
 * Provides standardized methods for API operations
 * All service classes should extend this base class
 */
export class BaseApiService {
  constructor(endpoint) {
    this.endpoint = endpoint
  }

  /**
   * Generic GET request
   * @param {string} path - Additional path to append to endpoint
   * @param {object} params - Query parameters
   * @returns {Promise} API response
   */
  async get(path = '', params = {}) {
    try {
      const url = path ? `${this.endpoint}/${path}` : this.endpoint
      const response = await apiClient.get(url, { params })
      return this.handleResponse(response)
    } catch (error) {
      throw this.handleError(error)
    }
  }

  /**
   * Generic POST request
   * @param {object} data - Request payload
   * @param {string} path - Additional path to append to endpoint
   * @param {object} additionalConfig - Additional axios config (headers, onUploadProgress, etc.)
   * @returns {Promise} API response
   */
  async post(data = {}, path = '', additionalConfig = {}) {
    try {
      const url = path ? `${this.endpoint}/${path}` : this.endpoint
      
      const response = await apiClient.post(url, data, additionalConfig)
      return this.handleResponse(response)
    } catch (error) {
      throw this.handleError(error)
    }
  }

  /**
   * Generic PUT request
   * @param {string|number} id - Resource ID
   * @param {object} data - Request payload
   * @param {string} path - Additional path to append to endpoint
   * @returns {Promise} API response
   */
  async put(id, data = {}, path = '') {
    try {
      const url = path ? `${this.endpoint}/${id}/${path}` : `${this.endpoint}/${id}`
      env.debug('[BaseApiService] PUT request to:', url)
      env.debug('[BaseApiService] ID:', id, 'Type:', typeof id)
      env.debug('[BaseApiService] Data:', data)
      
      const config = {}
      const response = await apiClient.put(url, data, config)
      return this.handleResponse(response)
    } catch (error) {
      throw this.handleError(error)
    }
  }

  /**
   * Generic PATCH request
   * @param {string|number} id - Resource ID
   * @param {object} data - Request payload
   * @param {string} path - Additional path to append to endpoint
   * @returns {Promise} API response
   */
  async patch(id, data = {}, path = '') {
    try {
      const url = path ? `${this.endpoint}/${id}/${path}` : `${this.endpoint}/${id}`
      const response = await apiClient.patch(url, data)
      return this.handleResponse(response)
    } catch (error) {
      throw this.handleError(error)
    }
  }

  /**
   * Generic DELETE request
   * @param {string|number} id - Resource ID
   * @returns {Promise} API response
   */
  async delete(id) {
    try {
      const url = `${this.endpoint}/${id}`
      const response = await apiClient.delete(url)
      return this.handleResponse(response)
    } catch (error) {
      throw this.handleError(error)
    }
  }

  /**
   * Generic request method for custom HTTP operations
   * @param {string} method - HTTP method (GET, POST, PUT, PATCH, DELETE)
   * @param {string} path - API path
   * @param {object} data - Request data
   * @param {object} config - Axios config options
   * @returns {Promise} API response
   */
  async request(method, path, data = null, config = {}) {
    try {
      const url = path ? `${this.endpoint}/${path}` : this.endpoint
      const requestConfig = {
        method,
        url,
        ...config
      }
      
      if (data) {
        if (method.toUpperCase() === 'GET') {
          requestConfig.params = data
        } else {
          requestConfig.data = data
        }
      }
      
      const response = await apiClient.request(requestConfig)
      return this.handleResponse(response)
    } catch (error) {
      throw this.handleError(error)
    }
  }

  /**
   * Get all resources with optional filters
   * @param {object} filters - Query filters
   * @returns {Promise} API response
   */
  async getAll(filters = {}) {
    return this.get('', filters)
  }

  /**
   * Get single resource by ID
   * @param {string|number} id - Resource ID
   * @returns {Promise} API response
   */
  async getById(id) {
    return this.get(id)
  }

  /**
   * Create new resource
   * @param {object} data - Resource data
   * @returns {Promise} API response
   */
  async create(data) {
    return this.post(data)
  }

  /**
   * Update existing resource
   * @param {string|number} id - Resource ID
   * @param {object} data - Updated resource data
   * @returns {Promise} API response
   */
  async update(id, data) {
    return this.put(id, data)
  }

  /**
   * Partially update existing resource
   * @param {string|number} id - Resource ID
   * @param {object} data - Partial resource data
   * @returns {Promise} API response
   */
  async partialUpdate(id, data) {
    return this.patch(id, data)
  }

  /**
   * Delete resource
   * @param {string|number} id - Resource ID
   * @returns {Promise} API response
   */
  async remove(id) {
    return this.delete(id)
  }

  /**
   * Handle successful API response
   * @param {object} response - Axios response object
   * @returns {object} Processed response data
   */
  handleResponse(response) {
    // Return the data directly if it's already in a standard format
    if (response.data) {
      return response.data
    }
    
    // Return whole response if data is not available
    return response
  }

  /**
   * Handle API errors
   * @param {object} error - Axios error object
   * @returns {object} Formatted error object
   */
  handleError(error) {
    // Log 403 as warning (permission issue), everything else as error
    if (error.response?.status === 403) {
      logger.warn('API permission denied', {
        requestId: error.config?._requestId,
        endpoint: this.endpoint,
        url: error.config?.url,
        message: error.response?.data?.message || error.message
      })
    } else {
      logger.error('API service error', {
        requestId: error.config?._requestId,
        endpoint: this.endpoint,
        error
      })
    }

    // Network error
    if (!error.response) {
      return {
        success: false,
        message: error.message || 'Network error occurred',
        errors: {},
        status: 0,
        response: null
      }
    }

    // Server responded with error
    const { status, data } = error.response

    // Format error response — preserve `response` so callers can check status codes
    const formattedError = {
      success: false,
      message: data?.message || this.getDefaultErrorMessage(status),
      errors: data?.errors || {},
      status,
      data: data || null,
      response: error.response,  // preserve original response for status checks (e.g. error?.response?.status !== 403)
      _permissionDenied: status === 403  // flag for isPermissionError() utility
    }

    return formattedError
  }

  /**
   * Get default error message based on status code
   * @param {number} status - HTTP status code
   * @returns {string} Error message
   */
  getDefaultErrorMessage(status) {
    const messages = {
      400: 'Bad request',
      401: 'Unauthorized access',
      403: 'Access forbidden',
      404: 'Resource not found',
      422: 'Validation failed',
      429: 'Too many requests',
      500: 'Internal server error',
      502: 'Bad gateway',
      503: 'Service unavailable',
      504: 'Gateway timeout'
    }

    return messages[status] || 'An error occurred'
  }

  /**
   * Build query string from object
   * @param {object} params - Query parameters
   * @returns {string} Query string
   */
  buildQueryString(params) {
    const queryParams = new URLSearchParams()
    
    Object.keys(params).forEach(key => {
      const value = params[key]
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => queryParams.append(`${key}[]`, v))
        } else {
          queryParams.append(key, value)
        }
      }
    })
    
    const queryString = queryParams.toString()
    return queryString ? `?${queryString}` : ''
  }
}

export default BaseApiService
